import React, { useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import LayoutContainer from 'components/Common/LayoutContainer'
import { mediaQueryUp } from 'components/utils/breakpoint'

import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import {
  getCurrentAvailableRates,
  getLowestAvailableStayPayTier,
  getPropertyCombinedRatesByRoom,
  getPropertyMappedBedbankOffer,
} from 'selectors/offerPage/offerPageSelectors'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ContactContext from 'contexts/contactContext'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Plural from 'components/utils/Formatters/Plural'
import PropertyRoomCheapestOptionMobile from './PropertyRoomCheapestOptionMobile'
import Pane from 'components/Common/Pane'
import Heading from 'LuxKit/Typography/Heading'
import type { RoomRateViews } from '../singlePropertyUtils'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'

const PackagesLayoutContainer = styled(LayoutContainer)`
  padding: 0;

  ${mediaQueryUp.desktop} {
    padding: 0 ${rem(16)};
  }
`

interface Props {
  offer: App.HotelOffer;
  bedbankOffer?: App.BedbankOffer;
  combinedRatesByRoom: Array<RoomRateViews>;
  hasRateError: boolean;
  checkIn?: string;
  lowestAvailableStayPayTier?: App.StayPayTier | null;
}

function PropertyRoomOptionsMobile(props: Props) {
  const {
    offer,
    bedbankOffer,
    combinedRatesByRoom,
    hasRateError,
    checkIn,
    lowestAvailableStayPayTier,
  } = props

  /************************************************************************************************/
  /* STOP! Do not add any more hooks for deriving packages/rooms or options/rates to this         */
  /* component! This logic is being refactored out into selectors! If you _need_ to add new       */
  /* logic, speak to Xiao or Andrew.                                                              */
  /************************************************************************************************/

  const supportContact = useContext(ContactContext)

  const highlightedPackage = useMemo(
    () => combinedRatesByRoom.find(({ isHighlighted }) => isHighlighted),
    [combinedRatesByRoom],
  )

  return (<VerticalSpacer gap={16}>
    <LayoutContainer>
      <VerticalSpacer gap={16}>
        <Heading variant="heading4">Room Options</Heading>
        {hasRateError && <MessageBanner
          kind="warning"
          tabletAlign="center"
          heading={`The third party booking system for ${offer.property?.name} is currently down`}
          description={`Please call our VIP team on ${supportContact.local.humanReadable} who are ready to provide assistance to complete your booking`}
        />}
      </VerticalSpacer>
    </LayoutContainer>

    {!hasRateError && <PackagesLayoutContainer>
      <VerticalSpacer gap={32}>
        {!checkIn && lowestAvailableStayPayTier && <MessageBanner
          kind="promotional-primary"
          icon={<LineInfoCircleIcon />}
          description={<>
            <b>Special offer:</b> Enjoy free nights for stays longer than <Plural count={lowestAvailableStayPayTier.stayNights} singular="day" withCount />. Select dates to check eligibility.
          </>}
        />}
        <Pane background="neutral-six">
          <VerticalSpacer gap={8}>
            {highlightedPackage && !!highlightedPackage.displayRates.length && <PropertyRoomCheapestOptionMobile
              offer={offer}
              bedbankOffer={bedbankOffer}
              propertyRoom={highlightedPackage.propertyRoom}
              availableRooms={highlightedPackage.availableRooms}
              displayRates={highlightedPackage.displayRates}
              roomFitsCapacity={highlightedPackage.roomFitsCapacity}
              datesAvailable={highlightedPackage.datesAvailable}
              isHighlighted
              roomRateUnmatched={highlightedPackage.roomRateUnmatched}
              detailsModalMode="room-and-package"
            />}
            {combinedRatesByRoom.map((item) => {
              if (highlightedPackage && highlightedPackage.roomId === item.roomId) {
                return null
              }
              return (<PropertyRoomCheapestOptionMobile
                key={item.roomId}
                offer={offer}
                bedbankOffer={bedbankOffer}
                propertyRoom={item.propertyRoom}
                availableRooms={item.availableRooms}
                displayRates={item.displayRates}
                roomFitsCapacity={item.roomFitsCapacity}
                datesAvailable={item.datesAvailable}
                detailsModalMode="room-and-package"
              />)
            })}
          </VerticalSpacer>
        </Pane>
      </VerticalSpacer>
    </PackagesLayoutContainer>}
  </VerticalSpacer>
  )
}

type MappedProps = Pick<Props,
  'offer' |
  'bedbankOffer' |
  'combinedRatesByRoom' |
  'hasRateError' |
  'checkIn' |
  'lowestAvailableStayPayTier'
> & { open?: boolean }

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  offer: state.offer as App.HotelOffer,
  bedbankOffer: getPropertyMappedBedbankOffer(state, appState),
  combinedRatesByRoom: getPropertyCombinedRatesByRoom(state, appState),
  hasRateError: !!getCurrentAvailableRates(state, appState)?.error,
  checkIn: state.checkInDate,
  lowestAvailableStayPayTier: getLowestAvailableStayPayTier(state, appState),
  open: !!state.activeFilter,
}), PropertyRoomOptionsMobile, preventRenderWithOpenFilter)
